"use client";

// FILE: src/features/nmck-calculator/components/chop-search-modal.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT:
// PURPOSE: Модальное окно поиска ЧОП для замены в Step 4 калькулятора НМЦК
// SCOPE: Поиск по seo_companies, debounce 300ms, выбор ЧОП
// INPUT: open/onOpenChange, onSelect(chop), excludeIds (уже выбранные)
// OUTPUT: React-разметка модального окна с поиском и результатами
// KEYWORDS: DOMAIN(NMCC): ChopSearch; TECH(React): ClientComponent; TECH(Debounce): 300ms
// END_MODULE_CONTRACT

import { useState, useEffect, useRef } from "react";
import { Search, Loader2, Building2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@oxpa/shared/ui/dialog";
import { Input } from "@oxpa/shared/ui/input";
import { Button } from "@oxpa/shared/ui/button";
import type { ChopCandidate } from "@/app/api/calculator/chops/route";

interface ChopSearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (chop: ChopCandidate) => void;
  excludeIds?: string[];
}

export function ChopSearchModal({ open, onOpenChange, onSelect, excludeIds }: ChopSearchModalProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ChopCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/calculator/chops/search?q=${encodeURIComponent(trimmed)}`);
        const json = await res.json();
        if (json.success) {
          let filtered = json.data as ChopCandidate[];
          if (excludeIds?.length) {
            filtered = filtered.filter((c: ChopCandidate) => !excludeIds.includes(c.id));
          }
          setResults(filtered);
        }
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, excludeIds]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-black border border-white/20 text-white font-mono rounded-none max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[10px] uppercase tracking-widest text-white font-bold">
            Поиск ЧОП для замены
          </DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input
            placeholder="Название или ИНН..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="rounded-none bg-white/5 border-white/20 text-white font-mono h-12 pl-10"
          />
        </div>

        <div className="space-y-2 mt-2">
          {loading && (
            <div className="flex items-center justify-center py-8 text-white/40">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              <span className="text-xs uppercase tracking-widest">Поиск...</span>
            </div>
          )}

          {!loading && results.length === 0 && query.trim().length >= 2 && (
            <div className="text-center py-8 text-white/30 text-xs uppercase tracking-widest">
              Ничего не найдено
            </div>
          )}

          {results.map((chop) => (
            <button
              key={chop.id}
              type="button"
              data-testid="chop-search-result"
              onClick={() => { onSelect(chop); onOpenChange(false); }}
              className="w-full text-left p-3 border border-white/10 bg-white/5 hover:border-green-500 hover:bg-green-500/10 transition-colors"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-white truncate">
                    {chop.name_short}
                  </div>
                  {chop.contact_email && (
                    <div className="text-xs text-white/50 truncate mt-0.5">
                      {chop.contact_email}
                    </div>
                  )}
                </div>
                <div className="shrink-0 flex items-center gap-1 px-2 py-1 border border-white/20 text-[10px] font-bold font-mono text-white/80">
                  <Building2 className="h-3 w-3" />
                  {chop.trust_score ?? "—"}
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="flex justify-end pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-none border-white/20 text-white/60 hover:text-white h-10 px-4 text-[10px] uppercase tracking-widest"
          >
            Закрыть
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
